import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from '@/lib/api/client';
import { useSession } from '@/lib/auth/session';

/**
 * 내 출퇴근 — 마이페이지 연차관리의 출퇴근 칸 (사용자 지시 2026-09-22:
 * 「개인 출퇴근도 여기서 관리할 수 있도록 하자. 실제 출결은 APP에서 할거고」).
 *
 * <h2>앱과 같은 경로를 쓴다</h2>
 * 찍는 자리(`POST /v1/me/attendances`)는 앱이 쓰던 그 경로다. 웹용 경로를 따로 내면 같은 규칙
 * (하루 한 번씩 · 다음에 누를 것은 서버가 정한다)을 두 벌로 들게 된다.
 *
 * <h2>달을 통째로 받는다</h2>
 * 앱의 첫 화면은 「최근 N 건」 이면 되지만, 달력을 그리고 <b>공휴일에 일한 날</b>을 세려면 그 달을
 * 다 봐야 한다. 공휴일도 같은 응답에 실려 온다 — 따로 부르면 두 응답의 기준 달이 어긋나는 순간
 * 「일했는데 후보에 없다」 가 생긴다.
 */

export type AttendanceRow = {
  id: string;
  personName: string;
  deptSite: string | null;
  /** IN(출근) · OUT(퇴근) */
  kind: 'IN' | 'OUT';
  occurredAt: string;
  locationName: string | null;
  mocked: boolean;
  note: string | null;
  /** 정정 승인으로 세우거나 옮긴 줄. 앱이 찍은 것과 구분해 그린다. */
  corrected: boolean;
};

export type MyAttendanceMonth = {
  month: string;
  rows: AttendanceRow[];
  /** 그 달의 공휴일. `worked` 가 참이면 그날 찍힌 줄이 있다 — 대체휴무 후보다. */
  holidays: Array<{ date: string; name: string; worked: boolean }>;
};

export type MyAttendanceNow = {
  /** 지금 눌러야 하는 것. <b>null 이면 오늘은 끝</b>이다(퇴근까지 찍었다). */
  nextKind: 'IN' | 'OUT' | null;
  today: AttendanceRow | null;
  recent: AttendanceRow[];
};

export type AttendanceCorrection = {
  id: string;
  targetDate: string;
  wantedIn: string | null;
  wantedOut: string | null;
  reason: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  reviewNote: string | null;
  reviewedAt: string | null;
  createdAt: string;
};

const NOW_KEY = ['me-attendance-now'];
const MONTH_KEY = 'me-attendance-month';
const CORRECTION_KEY = ['me-attendance-corrections'];

export function useMyAttendanceNow() {
  const { data: session } = useSession();
  return useQuery({
    queryKey: [...NOW_KEY, session?.user?.id ?? null],
    queryFn: () => apiFetch<MyAttendanceNow>('/v1/me/attendances'),
    enabled: !!session?.user?.id,
  });
}

export function useMyAttendanceMonth(month: string) {
  const { data: session } = useSession();
  return useQuery({
    queryKey: [MONTH_KEY, month, session?.user?.id ?? null],
    queryFn: () => apiFetch<MyAttendanceMonth>(`/v1/me/attendances/month?month=${encodeURIComponent(month)}`),
    enabled: !!session?.user?.id,
  });
}

/**
 * 웹에서 찍기 (사용자 결정 2026-09-22).
 *
 * <p><b>좌표를 보내지 않는다.</b> PC 에는 GPS 가 없고 브라우저 위치는 기지국·IP 추정이라
 * 「사무실에 있었다」 의 근거가 못 된다. 없는 값을 지어내 보내느니 비워 두고, 서버가 남기는
 * IP 와 시각이 그 줄의 전부가 되게 한다 — 무엇을 아는지가 분명해야 나중에 판단할 수 있다.
 */
export function usePunch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (kind: 'IN' | 'OUT') =>
      apiFetch<AttendanceRow>('/v1/me/attendances', { method: 'POST', body: { kind } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: NOW_KEY });
      void queryClient.invalidateQueries({ queryKey: [MONTH_KEY] });
    },
  });
}

export function useMyCorrections() {
  const { data: session } = useSession();
  return useQuery({
    queryKey: [...CORRECTION_KEY, session?.user?.id ?? null],
    queryFn: () => apiFetch<AttendanceCorrection[]>('/v1/me/attendances/corrections'),
    enabled: !!session?.user?.id,
  });
}

export type CorrectionWrite = {
  targetDate: string;
  wantedIn: string | null;
  wantedOut: string | null;
  reason: string;
};

export function useRequestCorrection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CorrectionWrite) =>
      apiFetch<AttendanceCorrection>('/v1/me/attendances/corrections', { method: 'POST', body }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: CORRECTION_KEY }),
  });
}

export function useWithdrawCorrection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetch<void>(`/v1/me/attendances/corrections/${id}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: CORRECTION_KEY }),
  });
}

// ── 자발적 근무 신청 (V556 · 인사총무팀 승인) ─────────────────────────────────

/**
 * 한 장 — 승인되면 그 시간대에 PC 가 잠기지 않고 52시간 셈에서 빠진다.
 * status: REQUESTED(확인 중) · APPROVED(승인) · REJECTED(반려).
 */
export type VoluntaryWork = {
  id: string;
  userId: string;
  personName: string | null;
  deptSite: string | null;
  workDate: string;
  startAt: string;
  endAt: string;
  content: string | null;
  status: 'REQUESTED' | 'APPROVED' | 'REJECTED';
  reviewNote: string | null;
  reviewedAt: string | null;
  createdAt: string;
};

const VOLUNTARY_KEY = ['me-voluntary-works'];

export function useMyVoluntaryWorks() {
  const { data: session } = useSession();
  return useQuery({
    queryKey: [...VOLUNTARY_KEY, session?.user?.id ?? null],
    queryFn: () => apiFetch<VoluntaryWork[]>('/v1/me/attendances/voluntary-works'),
    enabled: !!session?.user?.id,
  });
}

export function useRequestVoluntaryWork() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { workDate: string; start: string; end: string; content: string }) =>
      apiFetch<VoluntaryWork>('/v1/me/attendances/voluntary-works', { method: 'POST', body }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: VOLUNTARY_KEY }),
  });
}

export function useWithdrawVoluntaryWork() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetch<void>(`/v1/me/attendances/voluntary-works/${id}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: VOLUNTARY_KEY }),
  });
}
